import { FormikTouched } from 'formik';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import * as _ from 'underscore';
import { BaseReactSettingsCtrlProps } from '../../controllers/BaseReactSettingsCtrl';
import { Formik, FormikErrors, FormikHelpers, useFormikContext } from '../../FormikWrapperForStubbing';
import { SettingsComponentRegistry } from './SettingsComponentRegistry';

export interface SectionContainerBaseProps {
  sections: string[];
}

function wireFormikCallbacks(props: BaseReactSettingsCtrlProps) {
  // get formik specific functionality from the context. This is cleaner than polluting props with an HOC.
  const formik = useFormikContext<BaseReactSettingsCtrlProps>();

  // wrapper callback for submitForm because it has a different signature than what setSaveCallback expects
  const submitFormCallback = useCallback(() => {
    formik.submitForm();
  }, [formik.submitForm]);

  useEffect(() => {
    props.setSaveCallback(submitFormCallback);
    props.setCancelCallback(() => {
      formik.resetForm();
      formik.validateForm();
    });
    props.setOpenNavigationDialogCallback(_.noop);
    props.setIsBusyCallback(() => formik.isSubmitting);
  }, []);

  useEffect(() => {
    props.setIsFormDirty(formik.dirty);
    props.setIsFormValid(formik.isValid);
    props.setIsBusyCallback(() => formik.isSubmitting);
    props.mergeIntoParentScope?.(formik.values);
  }, [formik.isSubmitting, formik.dirty, formik.isValid, formik.values, formik.validateForm]);
}

function handleSubmit(values: BaseReactSettingsCtrlProps, actions: FormikHelpers<BaseReactSettingsCtrlProps>) {
  actions.setSubmitting(false);
  actions.resetForm({values});
  actions.validateForm();
}

function SettingsSectionList<Props extends BaseReactSettingsCtrlProps>(props: Props & SectionContainerBaseProps) {
  wireFormikCallbacks(props);
  const {sections, ...rest} = props;
  return <> {props.sections?.map((key, index) => {
    const Section = SettingsComponentRegistry[key];
    return <Section.component key={index} {...rest}/>;
  })}
  </>;
}

export function SettingsFormikContainer<Props extends BaseReactSettingsCtrlProps & {settings: any}>(props: Props & SectionContainerBaseProps & {
  initErrors: FormikErrors<Props>;
  initTouched: FormikTouched<Props>;
}) {
  // filter values down to migrated settings.
  const {sections, ...rest} = props;

  return <Formik initialValues={rest}
                 onSubmit={handleSubmit}
                 enableReinitialize={true}
                 validateOnMount={true}
                 initialTouched={props.initTouched}
                 initialErrors={props.initErrors}>
      <SettingsSectionList {...props}/>
  </Formik>;
}
